import React from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { useMe } from "~/api2/hooks/useMe"
import DynamicForm from "./DynamicForm"
import Button from "../../Button"
import titleCase from "~/utils/titleCase"
import { useDynamicFormsSchema } from "~/hooks/useDynamicFormsSchema"
import { useDynamicFormsResource } from "~/hooks/useDynamicFormsResource"

const DynamicFormEdit = () => {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { data: { camp_id: campId } } = useMe()
  const { index: { data: dynamicFormSchema } } = useDynamicFormsSchema()
  
  const pathSegments = location.pathname.split('/')
  const modelSlug = pathSegments[pathSegments.length - 2]
  
  const schema = dynamicFormSchema?.models?.find(s => {
    const schemaSlug = s.model.toLowerCase().replace(/[\s_]+/g, '-').replace(/[^\w-]+/g, '')
    return schemaSlug === modelSlug
  })
  
  const model = schema?.model || modelSlug
  
  const { show } = useDynamicFormsResource(model, campId, undefined, id)

  if (show.isLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="h-8 bg-gray-200 rounded w-64 animate-pulse"></div>
          <div className="h-10 bg-gray-200 rounded w-32 animate-pulse"></div>
        </div>
        
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
          <div className="grid grid-cols-2 gap-6 space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
              </div>
            ))}
          </div>
          
          <div className="flex gap-2 mt-6">
            <div className="h-10 bg-gray-200 rounded w-20 animate-pulse"></div>
            <div className="h-10 bg-gray-200 rounded w-24 animate-pulse"></div>
          </div>
        </div>
      </div>
    )
  }

  if (show.isError) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-8 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error loading resource</h2>
          <p className="text-gray-600 mb-6">There was a problem loading this resource. Please try again.</p>
          <Button onClick={() => navigate(`/admin/settings/${modelSlug}`)} className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
            Go Back
          </Button>
        </div>
      </div>
    )
  }

  if (!show.data?.record) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-8 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
            <svg className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.47-.881-6.08-2.33" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Resource not found</h2>
          <p className="text-gray-600 mb-6">The resource you're looking for doesn't exist or has been removed.</p>
          <Button onClick={() => navigate(`/admin/settings/${modelSlug}`)} className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
            Go Back
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">{titleCase(model)} - {show.data.record.id}</h2>
        <Button onClick={() => navigate(`/admin/settings/${modelSlug}`)} className="bg-gray-500 text-white px-3 py-1 rounded">
          Back to List
        </Button>
      </div>
      <DynamicForm
        campId={campId}
        model={model}
        init={show.data.record}
        onSaved={(record) => {
          console.log("Resource saved:", record)
          // Optionally navigate back to list or stay on edit page
        }}
      />
    </div>
  )
}

export default DynamicFormEdit 